import React from 'react';
import { Link } from 'react-router-dom';
import { Layout, Shield, Palette, Box, TrendingUp, Briefcase, Check } from 'lucide-react';
import SEO from '../components/SEO';
import './Services.css';

const Services = () => {
  const allServices = [
    {
      id: "web-dev",
      icon: <Layout size={40} />,
      title: "Static Web Development",
      features: ["SEO Optimized Architecture", "Headless CMS Integration", "Lightning Fast Loading"],
      desc: "We don't just build websites; we engineer high-performance digital storefronts that achieve perfect Google PageSpeed scores for maximum conversion."
    },
    {
      id: "qa",
      icon: <Shield size={40} />,
      title: "Software Testing & QA",
      features: ["Manual & Automation Testing", "Regression & Smoke Testing", "API Testing (Postman)"],
      desc: "No matter how complex your codebase is, our meticulous testing protocols identify bottlenecks and bugs to ensure a bulletproof product launch."
    },
    {
      id: "design",
      icon: <Palette size={40} />,
      title: "Graphic Design & UI/UX",
      features: ["Brand Identity Design", "Marketing Collaterals", "Prototyping in Figma"],
      desc: "Elevate your brand with functional aesthetics. We combine user psychology with modern design trends to create interfaces that drive engagement."
    },
    {
      id: "pm",
      icon: <Box size={40} />,
      title: "Product Management",
      features: ["Agile Roadmap Planning", "Stakeholder Management", "Feature Prioritization", "MVP Development"],
      desc: "Bridging the gap between idea and execution. We manage your product roadmap with precision, ensuring development stays on track and within scope."
    },
    {
      id: "business",
      icon: <Briefcase size={40} />,
      title: "Business Consulting",
      features: ["Operational Scaling", "Cost Optimization", "Market Entry Strategy", "Workflow Automation"],
      desc: "Providing the strategic framework and expert guidance needed to scale your operations from local startups to global market leaders."
    },
    {
      id: "marketing",
      icon: <TrendingUp size={40} />,
      title: "Marketing Consultation",
      features: ["Performance Marketing", "Content Strategy", "Social Media ROI", "Lead Generation Funnels"],
      desc: "Shift your focus from vanity metrics to real growth. We design data-driven marketing blueprints that connect your brand with the right audience."
    }
  ];

  return (
    <div className="services-container">
      <SEO 
        title="Services & Tech Stack | Vektrixo Digital Pune" 
        description="Explore Vektrixo Digital's professional services: high-performance static web development (React, Next.js), automated & manual software testing (QA), Figma UI/UX, product management, and business consulting in India."
        keywords="Software Testing Services, Web Development Services Pune, QA Automation, UI/UX Design, Product Management Pune, Business Consulting, React Developer, Next.js Developer"
        canonical="https://www.vektrixo.com/services"
      />
      <header className="services-header">
        <h1>Our <span className="highlight">Expertise</span></h1>
        <p>Comprehensive solutions tailored for the 2026 digital economy.</p>
      </header>

      <section className="services-detailed-grid">
        {allServices.map((service) => (
          <div key={service.id} className="detailed-card">
            <div className="card-icon">{service.icon}</div>
            <div className="card-content">
              <h2>{service.title}</h2>
              <p>{service.desc}</p>
              <ul className="feature-list">
                {service.features.map((feature, index) => (
                  <li key={index}><Check size={16} className="check-icon" /> {feature}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </section>

      {/* Starter Packages Section */}
      <section className="packages-section">
        <h2 className="section-title">Starter <span className="highlight">Packages</span></h2>
        <p className="section-subtitle">Predictable, value-packed scopes designed to kickstart our partnership.</p>
        
        <div className="packages-grid">
          <div className="package-card">
            <h3>Website Speed Fix</h3>
            <div className="package-price">Speed Booster</div>
            <p className="package-desc">We make your website load instantly so you don't lose potential customers.</p>
            <ul className="package-features">
              <li><Check size={14} className="check-icon" /> Guaranteed 1-second load time</li>
              <li><Check size={14} className="check-icon" /> Image & code optimization</li>
              <li><Check size={14} className="check-icon" /> Search engine (SEO) setup</li>
            </ul>
            <Link to="/contact" className="package-btn">Get Started</Link>
          </div>

          <div className="package-card featured">
            <div className="package-badge">Popular</div>
            <h3>Custom Website Build</h3>
            <div className="package-price">Design & Code</div>
            <p className="package-desc">We design and build a beautiful, modern website from scratch for your business.</p>
            <ul className="package-features">
              <li><Check size={14} className="check-icon" /> Premium modern designs</li>
              <li><Check size={14} className="check-icon" /> Works perfectly on mobile phones</li>
              <li><Check size={14} className="check-icon" /> Integrated contact forms</li>
            </ul>
            <Link to="/contact" className="package-btn">Get Started</Link>
          </div>

          <div className="package-card">
            <h3>Quality Check & Testing</h3>
            <div className="package-price">Bug Prevention</div>
            <p className="package-desc">We test your app thoroughly, find hidden bugs, and make sure it runs without crashing.</p>
            <ul className="package-features">
              <li><Check size={14} className="check-icon" /> Automated bug-detection setup</li>
              <li><Check size={14} className="check-icon" /> Testing of all key user flows</li>
              <li><Check size={14} className="check-icon" /> Full quality audit report</li>
            </ul>
            <Link to="/contact" className="package-btn">Get Started</Link>
          </div>
        </div>
      </section>

      <section className="process-section">
        <h2>How We <span className="highlight">Work</span></h2>
        <div className="process-steps">
          <div className="step"><span>01</span><h4>Discovery</h4></div>
          <div className="step"><span>02</span><h4>Strategy</h4></div>
          <div className="step"><span>03</span><h4>Execution</h4></div>
          <div className="step"><span>04</span><h4>Quality Check</h4></div>
        </div>
      </section>
    </div>
  );
};

export default Services;